import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TrendingDown, Activity, TrendingUp, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { useUnits } from '../../../contexts/UnitsContext'
import { computePhaseStats } from '../../../lib/phoenixUtils'
import { cn } from '../../../lib/utils'

const TYPE_ICON = { cut: TrendingDown, maintain: Activity, bulk: TrendingUp }
const TYPE_COLOR = {
  cut: 'bg-app-primary-100 text-app-primary-600 dark:bg-app-primary-900/30 dark:text-app-primary-400',
  maintain: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  bulk: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
}

export function CurrentPhaseCard({ phase, weightEntries }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const openManager = () => navigate('/phases')
  const { weightUnit, formatWeight } = useUnits()
  if (!phase) return null
  const stats = computePhaseStats(phase, weightEntries)
  if (!stats) return null

  const Icon = TYPE_ICON[phase.phase_type] || Activity
  const typeLabel = t(`phoenixTracker.phases.type.${phase.phase_type}`)
  const title = phase.name || typeLabel

  let deltaLabel = ''
  if (stats.currentWeight != null) {
    if (Math.abs(stats.delta) < 0.05) {
      deltaLabel = t('phoenixTracker.phases.stats.deltaNoChange')
    } else if (stats.delta > 0) {
      deltaLabel = t('phoenixTracker.phases.stats.deltaLost', {
        delta: formatWeight(stats.delta),
        unit: weightUnit,
      })
    } else {
      deltaLabel = t('phoenixTracker.phases.stats.deltaGained', {
        delta: formatWeight(Math.abs(stats.delta)),
        unit: weightUnit,
      })
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <div className={cn('rounded-full p-2 flex-shrink-0', TYPE_COLOR[phase.phase_type])}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
              {t('phoenixTracker.phases.currentTitle')}
            </p>
            <p className="text-sm font-semibold text-foreground truncate">
              {title}
              <span className="ms-2 text-xs font-normal text-muted-foreground">
                · {t('phoenixTracker.phases.stats.daysInto', { n: stats.daysInto })}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={openManager}
            className="text-xs text-app-primary-500 hover:text-app-primary-600 inline-flex items-center gap-0.5 flex-shrink-0"
          >
            {t('phoenixTracker.phases.manageLink')}
            <ChevronRight className="w-3 h-3 rtl:-scale-x-100" />
          </button>
        </div>

        {deltaLabel && (
          <p className="text-sm text-foreground">{deltaLabel}</p>
        )}

        {stats.progressPercent != null && (
          <div>
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{formatWeight(phase.start_weight_kg)} {weightUnit}</span>
              <span>{formatWeight(phase.goal_weight_kg)} {weightUnit}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-app-primary-500 transition-all"
                style={{ width: `${stats.progressPercent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1 text-center">
              {t('phoenixTracker.phases.stats.progress', {
                percent: Math.round(stats.progressPercent),
              })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
